import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import cn from "classnames";
import { useNavigateToDeviSubscribe } from "../../hooks/useNavigateToDeviSubscribe";
import { DeviBannerData } from "../../models/deviBannerModel";
import { TranslationObject } from "../../models/translationModal";
import { ProfilePayload } from "../../models/userModels";
import PostItem from "../../pages/PostsPage/Post";
import "./DeviBanner.scss";
import { getTranslation } from "../../untils/index";

const DEVI_BANNER_IMAGE = "/images/devi-banner.png";

interface DeviBannerProps {
    banner?: DeviBannerData;
    entities?: TranslationObject;
    modifier?: string;
    profile?: ProfilePayload;
    onCustomerBuyClick?: () => void;
}

/** Случайные даты вчерашнего дня: чем выше пост (меньше индекс), тем ближе дата к концу дня (к «сейчас») */
function getYesterdayDatesForItems(count: number): string[] {
    if (count === 0) return [];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dayStart = new Date(yesterday);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(yesterday);
    dayEnd.setHours(23, 59, 59, 999);
    const startMs = dayStart.getTime();
    const dayDuration = dayEnd.getTime() - startMs;
    const dates: string[] = [];
    for (let i = 0; i < count; i++) {
        const rev = count - 1 - i;
        const segmentStart = startMs + (rev / count) * dayDuration;
        const segmentEnd = startMs + ((rev + 1) / count) * dayDuration;
        const randomMs =
            segmentStart + Math.random() * (segmentEnd - segmentStart);
        dates.push(new Date(randomMs).toISOString().slice(0, 19));
    }
    return dates;
}

const DeviBanner: React.FC<DeviBannerProps> = ({
    banner,
    entities,
    modifier,
    profile,
    onCustomerBuyClick,
}) => {
    const t = (entities: any, code: string) => getTranslation(entities, code);
    const { currentLanguage } = useParams<{ currentLanguage?: string }>();
    const postsPath = `/${currentLanguage ?? "el"}/posts`;

    const handlePayClick = useNavigateToDeviSubscribe({
        profile,
        onCustomerBuyClick,
    });

    const subscribe = banner?.subscribe;
    const items = banner?.posts?.items ?? [];
    const visibleItems = items.slice(0, 2);
    const yesterdayDates = useMemo(
        () => getYesterdayDatesForItems(items.length),
        [items.length]
    );

    if (!banner) return null;

    const rootClassName = modifier
        ? `devi-preview devi-preview--${modifier}`
        : "devi-preview";

    return (
        <div className={rootClassName}>
            <div className="devi-preview__top">
                <div className="devi-preview__info">
                    <h2 className="devi-preview__title">
                        {t(entities, "devi_banner_title").replace(
                            ":count",
                            String(banner.count ?? 0)
                        )}
                    </h2>
                    <p className="devi-preview__description">
                        {t(entities, "devi_banner_description")}
                    </p>
                    <button
                        type="button"
                        className="devi-preview__paybtn"
                        onClick={handlePayClick}
                    >
                        {subscribe?.active === true
                            ? t(entities, "devi_banner_extend_subscription")
                            : t(entities, "devi_banner_button_buy")}
                    </button>
                </div>
                <div className="devi-preview__img-wrap">
                    <img
                        className="devi-preview__img"
                        src={DEVI_BANNER_IMAGE}
                        alt={t(entities, banner.title)}
                    />
                </div>
            </div>
            <div className="devi-preview__bottom">
                <h2 className="devi-preview__bottom-title">
                    {t(entities, "devi_banner_posts_title")}{" "}
                    <span>({banner.posts.count})</span>
                </h2>
                <div className="devi-preview__posts">
                    {visibleItems.map((item, index) => {
                        const postN = index + 1;
                        return (
                        <Link
                            key={index}
                            to={postsPath}
                            className="devi-preview__post-item-link"
                        >
                            <PostItem
                                post={{
                                    id: index,
                                    date: yesterdayDates[index] ?? "",
                                    provider: item.provider,
                                    title: t(
                                        entities,
                                        `devi_banner_post_${postN}_title`
                                    ),
                                    content: t(
                                        entities,
                                        `devi_banner_post_${postN}_description`
                                    ),
                                    link: postsPath,
                                }}
                                entities={entities}
                                className={cn(
                                    "devi-preview__post-item",
                                    index === 1 && "post-blur"
                                )}
                                disable={!subscribe?.active}
                            />
                        </Link>
                        );
                    })}
                </div>
                <Link to={postsPath} className="devi-preview__posts-link">
                    {t(entities, "devi_banner_button_all_orders")}
                </Link>
            </div>
        </div>
    );
};

export default DeviBanner;
